import { can, route } from '@/utils/index.jsx'
import {
  LayoutDashboard,
  Folder,
  BookUser,
  Settings,
  Fingerprint,
  Handshake,
  Contact,
  ClipboardPlus,
  FolderOpenDot,
  Cctv,
  Tickets,
  TicketPlus,
  Calendar,
  MicVocal,
  Users,
  FileUser,
  FileClock,
  MonitorCheck,
  CalendarDays
} from 'lucide-react'
import { renderToStaticMarkup } from 'react-dom/server'
import { Trans } from 'react-i18next'
import links from '../../links.json'

export const buildNavigation = (auth, url) => {
  const currentUrl = url.current.startsWith('/', 0) ? url.current : `/${url.current}`

  const navigation = [
    {
      text: <Trans i18nKey='labels.dashboard' />,
      path: '/',
      icon: renderToStaticMarkup(<LayoutDashboard size={16} />),
      show: true
    },
    {
      text: <Trans i18nKey='labels.check' />,
      icon: renderToStaticMarkup(<MonitorCheck size={16} />),
      path: route(links.accessControl.accessPassPreview.manage),
      show: can(auth, 'create_access_passes')
    },
    {
      text: <Trans i18nKey='labels.access_passes' />,
      icon: renderToStaticMarkup(<Tickets size={16} />),
      path: route(links.accessControl.accessPasses.index),
      show: can(auth, 'view_access_passes'),
      new: {
        path: route(links.accessControl.accessPasses.create),
        show: can(auth, 'create_access_passes')
      }
    },
    {
      text: <Trans i18nKey='labels.access_request_2' />,
      icon: renderToStaticMarkup(<TicketPlus size={16} />),
      path: route(links.accessControl.accessRequests.index),
      show: can(auth, 'view_access_requests'),
      new: {
        path: links.accessControl.accessRequests.create,
        show: can(auth, 'create_access_requests')
      }
    },
    {
      text: <Trans i18nKey='labels.calendar' />,
      icon: renderToStaticMarkup(<CalendarDays size={16} />),
      path: route(links.accessControl.accessRequestsCalendar.index),
      show: can(auth, 'view_calendar')
    },
    {
      text: <Trans i18nKey='modules.module_groups.access_ledger' />,
      icon: renderToStaticMarkup(<FileClock size={16} />),
      path: route(links.accessControl.accessLedger.index),
      show: can(auth, 'view_access_ledger')
    },
    {
      text: <Trans i18nKey='labels.invited_persons' />,
      icon: renderToStaticMarkup(<Calendar size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.event_invitations' />,
          path: links.accessControl.eventInvitations.index,
          show: can(auth, 'view_event_invitations'),
          new: {
            path: links.accessControl.eventInvitations.create,
            show: can(auth, 'create_event_invitations')
          }
        },
        {
          text: <Trans i18nKey='labels.events' />,
          path: route(links.accessControl.events.index),
          show: can(auth, 'view_events')
        },
        {
          text: <Trans i18nKey='labels.categories' />,
          path: route(links.accessControl.eventCategories.index),
          show: can(auth, 'view_event_categories')
        }
      ]
    },
    {
      text: <Trans i18nKey='labels.announced_visitors' />,
      icon: renderToStaticMarkup(<FileUser size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.access_announced_visits' />,
          path: links.accessControl.accessAnnouncedVisits.index,
          show: can(auth, 'view_access_announced_visits'),
          new: {
            path: links.accessControl.accessAnnouncedVisits.create,
            show: can(auth, 'create_access_announced_visits')
          }
        },
        {
          text: <Trans i18nKey='labels.code_books' />,
          items: [
            {
              text: <Trans i18nKey='labels.access_visitor_types' />,
              path: links.accessControl.accessVisitorTypes.index,
              show: can(auth, 'view_access_visitor_types')
            }
          ]
        }
      ]
    },
    {
      text: <Trans i18nKey='labels.journalists' />,
      icon: renderToStaticMarkup(<MicVocal size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.accreditations' />,
          path: links.documents.accreditationRequests.index,
          show: can(auth, 'view_accreditation_requests'),
          new: {
            path: links.documents.accreditationRequests.create,
            show: can(auth, 'create_accreditation_requests')
          }
        }
      ]
    },
    {
      text: <Trans i18nKey='labels.vendors' />,
      icon: renderToStaticMarkup(<Users size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.vendors' />,
          path: links.accounts.index + '?vendors-index',
          show: can(auth, 'view_accounts'),
          new: {
            path: links.accounts.create + '?vendors-create',
            show: can(auth, 'create_accounts')
          }
        },
        {
          text: <Trans i18nKey='labels.person_vehicle_check' />,
          path: links.documents.personVehicleCheckRequests.index,
          show: can(auth, 'view_person_vehicle_check_requests'),
          new: {
            path: links.documents.personVehicleCheckRequests.create,
            show: can(auth, 'create_person_vehicle_check_requests')
          }
        }
      ]
    },
    {
      text: <Trans i18nKey='modules.access_control' />,
      icon: renderToStaticMarkup(<Fingerprint size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.devices' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.access_groups' />,
              path: route(links.accessControl.accessGroups.index),
              show: can(auth, 'view_access_groups')
            },
            {
              text: <Trans i18nKey='modules.module_groups.access_devices' />,
              path: route(links.accessControl.accessDevices.index),
              show: can(auth, 'view_access_devices')
            },
            {
              text: <Trans i18nKey='modules.module_groups.access_device_locations' />,
              path: route(links.accessControl.accessDeviceLocations.index),
              show: can(auth, 'view_access_device_locations')
            },
            {
              text: <Trans i18nKey='modules.module_groups.access_users' />,
              path: route(links.accessControl.accessUsers.index),
              show: can(auth, 'view_access_users')
            },
            {
              text: <Trans i18nKey='modules.module_groups.access_function_keys' />,
              path: route(links.accessControl.accessFunctionKeys.index),
              show: can(auth, 'view_access_function_keys')
            }
          ]
        },
        {
          text: <Trans i18nKey='labels.access_passes' />,
          items: [
            {
              text: <Trans i18nKey='labels.requests' />,
              path: route(links.accessControl.accessRequests.index) + '?access-request',
              show: can(auth, 'view_access_requests'),
              new: {
                path: links.accessControl.accessRequests.create + '?access-request-create',
                show: can(auth, 'create_access_requests')
              }
            },
            {
              text: <Trans i18nKey='labels.access_passes' />,
              path: route(links.accessControl.accessPasses.index) + '?pass',
              show: can(auth, 'view_access_passes'),
              new: {
                path: route(links.accessControl.accessPasses.create) + '?pass-create',
                show: can(auth, 'create_access_passes')
              }
            }
          ]
        },
        {
          text: <Trans i18nKey='labels.code_books' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.access_pass_categories' />,
              path: route(links.accessControl.accessPassCategories.index),
              show: can(auth, 'view_access_pass_categories')
            },
            {
              text: <Trans i18nKey='modules.module_groups.access_reason_for_visits' />,
              path: route(links.accessControl.accessReasonForVisits.index),
              show: can(auth, 'view_access_reason_for_visits')
            }
          ]
        }
      ]
    },
    // {
    //   text: <Trans i18nKey='modules.time_attendance' />,
    //   icon: renderToStaticMarkup(<CalendarClock size={16} />),
    //   items: [
    //     {
    //       text: <Trans i18nKey='labels.shifts' />,
    //       items: [
    //         {
    //           text: <Trans i18nKey='modules.module_groups.break_timings' />,
    //           path: links.timeAttendances.breakTimings.index,
    //           show: can(auth, 'view_break_timings')
    //         },
    //         {
    //           text: <Trans i18nKey='modules.module_groups.weekly_off' />,
    //           path: links.timeAttendances.weeklyOffs.index,
    //           show: can(auth, 'view_weekly_off'),
    //           new: {
    //             path: links.timeAttendances.weeklyOffs.create,
    //             show: can(auth, 'create_weekly_off')
    //           }
    //         },
    //         {
    //           text: <Trans i18nKey='modules.module_groups.work_shifts' />,
    //           path: links.timeAttendances.workShifts.index,
    //           show: can(auth, 'view_work_shifts'),
    //           new: {
    //             path: links.timeAttendances.workShifts.create,
    //             show: can(auth, 'create_work_shifts')
    //           }
    //         }
    //       ]
    //     }
    //   ]
    // },
    {
      text: <Trans i18nKey='modules.video_surveillance' />,
      icon: renderToStaticMarkup(<Cctv size={16} />),
      items: [
        {
          text: <Trans i18nKey='modules.module_groups.surveillance_positions' />,
          path: links.videoSurveillance.surveillancePositions.index,
          show: can(auth, 'view_surveillance_positions')
        },
        {
          text: <Trans i18nKey='labels.devices' />,
          path: links.videoSurveillance.surveillanceDevices.index,
          show: can(auth, 'view_surveillance_positions')
        }
      ]
    },
    {
      text: <Trans i18nKey='modules.documents' />,
      icon: renderToStaticMarkup(<FolderOpenDot size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.requests' />,
          items: [
            {
              text: <Trans i18nKey='labels.accreditations' />,
              path: links.documents.accreditationRequests.index + '?accreditation-request',
              show: can(auth, 'view_accreditation_requests'),
              new: {
                path: links.documents.accreditationRequests.create + '?accreditation-request-create',
                show: can(auth, 'create_accreditation_requests')
              }
            },
            {
              text: <Trans i18nKey='labels.person_check' />,
              path: links.documents.personCheckRequests.index,
              show: can(auth, 'view_person_check_requests'),
              new: {
                path: links.documents.personCheckRequests.create,
                show: can(auth, 'create_person_check_requests')
              }
            },
            {
              text: <Trans i18nKey='labels.person_vehicle_check' />,
              path: links.documents.personVehicleCheckRequests.index + '?person-vehicle-check',
              show: can(auth, 'view_person_vehicle_check_requests'),
              new: {
                path: links.documents.personVehicleCheckRequests.create + '?person-vehicle-check-create',
                show: can(auth, 'create_person_vehicle_check_requests')
              }
            }
          ]
        }
      ]
    },
    {
      text: <Trans i18nKey='labels.purchase' />,
      icon: renderToStaticMarkup(<Handshake size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.contracts' />,
          path: links.contracts.index,
          show: can(auth, 'view_contracts'),
          new: {
            path: links.contracts.create,
            show: can(auth, 'create_contracts')
          }
        },
        {
          text: <Trans i18nKey='labels.vendors' />,
          path: links.accounts.index,
          show: can(auth, 'view_accounts'),
          new: {
            path: links.accounts.create + '?accounts-create',
            show: can(auth, 'create_accounts')
          }
        }
      ]
    },
    {
      text: <Trans i18nKey='modules.hr' />,
      icon: renderToStaticMarkup(<BookUser size={16} />),
      items: [
        {
          text: <Trans i18nKey='modules.module_groups.employees' />,
          path: route(links.hr.employees.index),
          show: can(auth, 'view_employees'),
          new: {
            path: links.hr.employees.create,
            show: can(auth, 'create_employees')
          }
        },
        {
          text: <Trans i18nKey='labels.systematisation' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.organizational_unit_types' />,
              path: links.hr.organizationalUnitTypes.index,
              show: can(auth, 'view_organizational_unit_types')
            },
            {
              text: <Trans i18nKey='labels.organizational_structure' />,
              path: route(links.hr.organizationalStructures.index),
              show: can(auth, 'view_organizational_structure')
            }
          ]
        },
        // {
        //   text: <Trans i18nKey='labels.requests' />,
        //   items: [
        //     {
        //       text: <Trans i18nKey='labels.absence' />,
        //       path: links.hr.absenceRequests.index,
        //       show: can(auth, 'view_absence_requests'),
        //       new: {
        //         path: links.hr.absenceRequests.create,
        //         show: can(auth, 'create_absence_requests')
        //       }
        //     }
        //   ]
        // },
        // {
        //   text: <Trans i18nKey='labels.affiliation' />,
        //   items: [
        //     {
        //       text: <Trans i18nKey='modules.module_groups.nationalities' />,
        //       path: links.hr.nationalities.index,
        //       show: can(auth, 'view_nationalities')
        //     },
        //     {
        //       text: <Trans i18nKey='modules.module_groups.religions' />,
        //       path: links.hr.religions.index,
        //       show: can(auth, 'view_nationalities')
        //     },
        //     {
        //       text: <Trans i18nKey='modules.module_groups.citizenship' />,
        //       path: links.hr.citizenship.index,
        //       show: can(auth, 'view_citizenship')
        //     }
        //   ]
        // },
        {
          text: <Trans i18nKey='modules.module_groups.job_positions' />,
          items: [
            {
              text: <Trans i18nKey='menu.categories' />,
              path: links.hr.jobTitleCategories.index,
              show: can(auth, 'view_job_title_categories')
            },
            {
              text: <Trans i18nKey='modules.module_groups.job_titles' />,
              path: links.hr.jobTitles.index,
              show: can(auth, 'view_job_titles')
            }
          ]
        }
        // {
        //   text: <Trans i18nKey='menu.employment_contracts' />,
        //   path: links.hr.employmentContracts.index,
        //   show: can(auth, 'view_employment_contracts')
        // }
        // {
        //   text: <Trans i18nKey='labels.absences' />,
        //   items: [
        // {
        //   text: <Trans i18nKey='labels.absences' />,
        //   path: links.hr.employeeAbsences.index,
        //   show: can(auth, 'view_employee_absences'),
        //   new: {
        //     path: links.hr.employeeAbsences.create,
        //     show: can(auth, 'create_employee_absences')
        //   }
        // },
        // {
        //   text: <Trans i18nKey='modules.module_groups.cause_of_absence_types' />,
        //   path: links.hr.causeOfAbsenceTypes.index,
        //   show: can(auth, 'view_cause_of_absence_types')
        // },
        // {
        //   text: <Trans i18nKey='modules.module_groups.cause_of_absences' />,
        //   path: links.hr.causeOfAbsences.index,
        //   show: can(auth, 'view_cause_of_absences')
        // },
        // {
        //   text: <Trans i18nKey='modules.module_groups.cause_of_inactivities' />,
        //   path: links.hr.causeOfInactivities.index,
        //   show: can(auth, 'view_cause_of_inactivities')
        // }
        // ]
        // },
        // {
        //   text: <Trans i18nKey="labels.setup"/>,
        //   items: [
        //     {
        //       text: <Trans i18nKey="modules.module_groups.nonworking_days"/>,
        //       path: links.hr.nonworkingDays.index,
        //       show: can(auth, 'view_nonworking_days')
        //     }
        //   ]
        // }
      ]
    },
    {
      text: <Trans i18nKey='labels.contacts' />,
      path: links.accounts.contacts.index,
      icon: renderToStaticMarkup(<Contact size={16} />),
      show: can(auth, 'view_contacts'),
      new: {
        path: links.accounts.contacts.create,
        show: can(auth, 'create_contacts')
      }
    },
    {
      text: <Trans i18nKey='labels.reports' />,
      icon: renderToStaticMarkup(<ClipboardPlus size={16} />),
      items: [
        {
          text: <Trans i18nKey='labels.working_hours' />,
          path: route(links.accessControl.reports.timeAttendanceRecord.index),
          show: can(auth, 'time_attendance_record')
        },
        {
          text: <Trans i18nKey='labels.records' />,
          items: [
            {
              text: <Trans i18nKey='labels.visitors' />,
              path: route(links.accessControl.reports.accessVisitors.index),
              show: can(auth, 'access_visitors_report')
            },
            {
              text: <Trans i18nKey='labels.vendors' />,
              path: route(links.accessControl.reports.accessVendors.index),
              show: can(auth, 'access_vendors_report')
            },
            {
              text: <Trans i18nKey='labels.employee_outside_working_hours' />,
              path: route(links.accessControl.reports.employeeOutsideWorkingHours.index),
              show: can(auth, 'access_employees_outside_working_hours')
            }
          ]
        }
      ]
    },
    {
      text: <Trans i18nKey='modules.administration' />,
      icon: renderToStaticMarkup(<Folder size={16} />),
      items: [
        // Company
        {
          text: <Trans i18nKey='labels.company' />,
          path: links.administration.company.edit,
          show: can(auth, 'edit_company')
        },
        // Users
        {
          text: <Trans i18nKey='modules.module_groups.users' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.roles' />,
              path: links.administration.roles.index,
              show: can(auth, 'view_roles')
            },
            {
              text: <Trans i18nKey='labels.users' />,
              path: links.administration.users.index,
              show: can(auth, 'view_users'),
              new: {
                path: links.administration.users.create,
                show: can(auth, 'create_users')
              }
            }
          ]
        },
        // Localization
        {
          text: <Trans i18nKey='labels.localization' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.countries' />,
              path: links.administration.countries.index,
              show: can(auth, 'view_countries')
            },
            {
              text: <Trans i18nKey='modules.module_groups.cities' />,
              path: links.administration.cities.index,
              show: can(auth, 'view_cities')
            },
            {
              text: <Trans i18nKey='modules.module_groups.post_codes' />,
              path: links.administration.postCodes.index,
              show: can(auth, 'view_post_codes')
            },
            {
              text: <Trans i18nKey='modules.module_groups.languages' />,
              path: links.administration.languages.index,
              show: can(auth, 'view_languages')
            }
          ]
        },
        {
          text: <Trans i18nKey='labels.banks' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.banks' />,
              path: links.administration.banks.index,
              show: can(auth, 'view_banks')
            },
            {
              text: <Trans i18nKey='modules.module_groups.bank_accounts' />,
              path: links.administration.bankAccounts.index,
              show: can(auth, 'view_bank_accounts')
            }
          ]
        },
        {
          text: <Trans i18nKey='labels.currencies' />,
          path: links.administration.currencies.index,
          show: can(auth, 'view_currencies')
        },
        {
          text: <Trans i18nKey='labels.unit_of_measures' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.unit_of_measures' />,
              path: links.administration.unitOfMeasures.index,
              show: can(auth, 'view_unit_of_measures')
            },
            {
              text: <Trans i18nKey='modules.module_groups.measurement_groups' />,
              path: links.administration.measurementGroups.index,
              show: can(auth, 'view_measurement_groups')
            }
          ]
        },
        {
          text: <Trans i18nKey='modules.module_groups.locations' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.locations' />,
              path: links.administration.locations.index,
              show: can(auth, 'view_locations')
            },
            {
              text: <Trans i18nKey='modules.module_groups.res_centers' />,
              path: links.administration.responsibilityCenters.index,
              show: can(auth, 'view_res_centers')
            }
          ]
        },
        // Shipment
        {
          text: <Trans i18nKey='labels.shipment' />,
          items: [
            {
              text: <Trans i18nKey='modules.module_groups.shipment_agents' />,
              path: links.administration.shipmentAgents.index,
              show: can(auth, 'view_shipment_agents')
            },
            {
              text: <Trans i18nKey='modules.module_groups.shipment_methods' />,
              path: links.administration.shipmentMethods.index,
              show: can(auth, 'view_shipment_methods')
            },
            {
              text: <Trans i18nKey='modules.module_groups.transport_methods' />,
              path: links.administration.transportMethods.index,
              show: can(auth, 'view_transport_methods')
            }
          ]
        },
        // Model control
        {
          text: <Trans i18nKey='modules.model_control' />,
          items: [
            // Numbering series
            {
              text: <Trans i18nKey='modules.module_groups.numbering_series' />,
              path: links.administration.numberingSeries.index,
              show: can(auth, 'view_numbering_series'),
              new: {
                path: links.administration.numberingSeries.create,
                show: can(auth, 'create_numbering_series')
              }
            },
            // Model Life Cycles
            {
              text: <Trans i18nKey='labels.model_life_cycles' />,
              path: links.administration.modelLifeCycles.index,
              show: can(auth, 'view_model_life_cycles')
            },
            // Model terms and conditions
            {
              text: <Trans i18nKey='labels.model_terms_and_conditions' />,
              path: links.administration.modelTermsAndConditions.index,
              show: can(auth, 'view_model_terms_and_conditions')
            },
            // Print templates
            {
              text: <Trans i18nKey='labels.templates' />,
              path: links.administration.printTemplates.index,
              show: can(auth, 'view_print_templates')
            }
          ]
        },
        // Documents
        {
          text: <Trans i18nKey='labels.documents' />,
          items: [
            // Contracts
            {
              text: <Trans i18nKey='labels.contracts' />,
              items: [
                // Contract types
                {
                  text: <Trans i18nKey='labels.contract_types' />,
                  path: links.administration.contractTypes.index,
                  show: can(auth, 'view_contract_types')
                },
                // Contract procedure types
                {
                  text: <Trans i18nKey='labels.contract_procedure_types' />,
                  path: links.administration.contractProcedureTypes.index,
                  show: can(auth, 'view_contract_procedure_types')
                }
              ]
            }
          ]
        },
        // OpenData
        {
          text: <Trans i18nKey='labels.open_data' />,
          path: links.administration.openData.index,
          show: can(auth, 'view_open_data')
        }
      ]
    },
    {
      text: <Trans i18nKey='modules.application' />,
      icon: renderToStaticMarkup(<Settings size={16} />),
      items: [
        // Modules
        {
          text: <Trans i18nKey='modules.module_groups.modules' />,
          path: links.application.modules.index,
          show: can(auth, 'view_modules')
        },
        // Settings
        {
          text: <Trans i18nKey='modules.module_groups.settings' />,
          path: links.application.settings.index,
          show: can(auth, 'view_settings')
        },
        {
          text: <Trans i18nKey='labels.company' />,
          path: links.application.company.edit,
          show: can(auth, 'edit_company')
        }
      ]
    }
  ]

  filterNavigation(navigation, currentUrl)

  return navigation
}

const filterNavigation = (navigation, currentUrl) => {
  const toSplice = []

  navigation.forEach((n, i) => {
    if (n.items) {
      filterNavigation(n.items, currentUrl)

      n.items.map(i => {
        i.selected = i.path === currentUrl
        i.expanded = i.path === currentUrl
        return i
      })

      n.show = n.items.filter(i => i.show).length > 0
    }

    if (!n.items) {
      n.selected = n.path === currentUrl
      n.expanded = n.path === currentUrl
    }

    if (!n.show) {
      toSplice.push(i)
    }
  })

  toSplice.reverse().forEach(spliceIndex => {
    navigation.splice(spliceIndex, 1)
  })
}
